/**
 * Search text normalization, shared by the backend index build and the admin
 * client.
 *
 * The build writes each document's `haystack` through `normalizeForSearch` and
 * the client normalizes the query the same way. Drift between the two would
 * make matches silently disappear rather than fail loudly, which is why this
 * lives in the shared package rather than in either app.
 */

/** Punctuation, symbols, and separators — replaced with a space, never removed. */
const NON_ALPHANUMERIC = /[^\p{L}\p{N}]+/gu;
const COMBINING_MARKS = /\p{M}+/gu;

/**
 * Letters NFD cannot decompose, because the stroke or ligature is part of the
 * base codepoint rather than a combining mark.
 *
 * Without these, `lodz` fails to match `Łódź` even though the accented `ó`
 * folds correctly — the surviving `ł` silently breaks the token.
 */
const UNDECOMPOSABLE: Record<string, string> = {
  ł: "l",
  ø: "o",
  đ: "d",
  ð: "d",
  æ: "ae",
  œ: "oe",
  ß: "ss",
  þ: "th",
  ħ: "h",
  ŧ: "t",
  ı: "i",
  ŀ: "l",
};

const UNDECOMPOSABLE_PATTERN = new RegExp(
  `[${Object.keys(UNDECOMPOSABLE).join("")}]`,
  "gu",
);

/**
 * Folds text to a comparable form: lowercased, diacritics reduced to base
 * letters, punctuation replaced by single spaces, and whitespace collapsed.
 *
 * Punctuation becomes a space rather than being deleted so `Smith&Sons`
 * tokenizes as two words; deleting it would yield `smithsons`, which no
 * natural query matches. Letters NFD cannot decompose are mapped explicitly
 * first, so `ł` and `ø` fold rather than surviving as unmatched letters.
 *
 * Idempotent: normalizing already-normalized text returns it unchanged.
 */
export function normalizeForSearch(input: string): string {
  return input
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(UNDECOMPOSABLE_PATTERN, (char) => UNDECOMPOSABLE[char] ?? char)
    .replace(NON_ALPHANUMERIC, " ")
    .trim();
}

/**
 * Normalizes then splits into unique tokens, preserving first-seen order.
 *
 * De-duplication keeps a repeated word from counting twice when scoring.
 */
export function tokenize(input: string): string[] {
  const normalized = normalizeForSearch(input);
  if (normalized === "") return [];
  return [...new Set(normalized.split(" "))];
}
